import { DURATION, EASE_OUT, reducedMotion } from '../../src/js/utils/motion';

const setup = (root: HTMLElement): void => {
  const seconds = Number(root.dataset.interval || 0);
  const items = Array.from(root.querySelectorAll<HTMLElement>('[data-showcase-item]'));
  const figures = Array.from(root.querySelectorAll<HTMLElement>('[data-showcase-figure]'));
  const triggers = items
    .map((item) => item.querySelector<HTMLButtonElement>('.showcase__trigger'))
    .filter((trigger): trigger is HTMLButtonElement => !!trigger);

  if (items.length < 2) {
    return;
  }

  const panelOf = (item: HTMLElement) => item.querySelector<HTMLElement>('.showcase__panel');
  const ringOf = (item: HTMLElement) => item.querySelector<SVGCircleElement>('[data-showcase-progress]');
  const animations = new WeakMap<HTMLElement, Animation>();

  let active = items.findIndex((item) => item.classList.contains('is-active'));
  let countdown: Animation | null = null;
  let onScreen = !('IntersectionObserver' in window);

  const startCountdown = (): void => {
    countdown?.cancel();
    countdown = null;

    if (!seconds || reducedMotion() || !onScreen) {
      return;
    }

    const ring = ringOf(items[active]);

    if (!ring) {
      return;
    }

    const length = ring.getTotalLength();

    ring.style.strokeDasharray = `${length}`;

    countdown = ring.animate(
      { strokeDashoffset: [`${length}`, '0'] },
      { duration: seconds * 1000, easing: 'linear', fill: 'forwards' }
    );

    countdown.onfinish = () => open((active + 1) % items.length);
  };

  const clearRings = (): void => {
    items.forEach((item) => {
      const ring = ringOf(item);

      if (ring) {
        const length = ring.getTotalLength();

        ring.style.strokeDasharray = `${length}`;
        ring.style.strokeDashoffset = `${length}`;
      }
    });
  };

  const fold = (panel: HTMLElement, open: boolean): void => {
    animations.get(panel)?.cancel();

    if (reducedMotion()) {
      panel.style.height = open ? 'auto' : '0px';
      return;
    }

    const from = panel.getBoundingClientRect().height;

    panel.style.height = 'auto';
    const to = open ? panel.getBoundingClientRect().height : 0;
    panel.style.height = `${from}px`;

    const animation = panel.animate(
      { height: [`${from}px`, `${to}px`], opacity: [open ? 0 : 1, open ? 1 : 0] },
      { duration: DURATION.base, easing: EASE_OUT }
    );

    animations.set(panel, animation);

    animation.onfinish = () => {
      animations.delete(panel);
      panel.style.height = open ? 'auto' : '0px';
      panel.style.opacity = '';
    };
  };

  const open = (index: number): void => {
    active = index;
    countdown?.cancel();
    clearRings();

    items.forEach((item, current) => {
      const active = current === index;
      const panel = panelOf(item);

      if (item.classList.contains('is-active') === active) {
        return;
      }

      item.classList.toggle('is-active', active);
      triggers[current]?.setAttribute('aria-expanded', String(active));

      if (panel) {
        fold(panel, active);
      }
    });

    figures.forEach((figure) => {
      figure.classList.toggle('is-active', figure.dataset.showcaseFigure === String(index));
    });

    startCountdown();
  };

  items.forEach((item, index) => {
    const panel = panelOf(item);

    if (panel) {
      panel.style.height = item.classList.contains('is-active') ? 'auto' : '0px';
    }

    triggers[index]?.addEventListener('click', () => open(index));
  });

  clearRings();

  if (active < 0) {
    active = 0;
  }

  const hold = () => countdown?.pause();
  const resume = () => {
    if (onScreen && countdown && 'paused' === countdown.playState) {
      countdown.play();
    }
  };

  root.addEventListener('pointerenter', hold);
  root.addEventListener('pointerleave', resume);
  root.addEventListener('focusin', hold);
  root.addEventListener('focusout', (event) => {
    if (!root.contains(event.relatedTarget as Node)) {
      resume();
    }
  });

  if (seconds && 'IntersectionObserver' in window) {
    new IntersectionObserver(
      (entries) => {
        onScreen = entries[0].isIntersecting;

        if (!onScreen) {
          countdown?.pause();
          return;
        }

        if (countdown) {
          countdown.play();
        } else {
          startCountdown();
        }
      },
      { threshold: 0 }
    ).observe(root);
  } else {
    startCountdown();
  }

  triggers.forEach((trigger, index) => {
    trigger.addEventListener('keydown', (event) => {
      const keys: Record<string, number> = {
        ArrowDown: index + 1,
        ArrowUp: index - 1,
        Home: 0,
        End: triggers.length - 1,
      };

      const next = keys[event.key];

      if (undefined === next) {
        return;
      }

      event.preventDefault();

      const target = triggers[(next + triggers.length) % triggers.length];

      target.focus();
      open(triggers.indexOf(target));
    });
  });
};

const init = (): void => {
  const roots = document.querySelectorAll<HTMLElement>('[data-showcase]');

  if (!roots.length) {
    return;
  }

  document.documentElement.classList.add('has-showcase-js');

  roots.forEach(setup);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

export {};
